/**
 * Bearer 인증 파일 다운로드가 media 쿠키를 함께 보내지 않게 하는 모듈이다.
 *
 * 쿠키(`__Host-ttmo_media`)는 `Path=/`라 같은 origin의 모든 XHR에 자동으로 실린다.
 * 서버는 파일 GET(`/api/file/download*`)에서 쿠키와 Authorization이 함께 오면 거부한다.
 * 기본 XHR adapter는 같은 origin 쿠키를 끌 수 없으므로, 이전 쿠키가 남을 수 있는 동일 HTTPS origin에서
 * `credentials: 'omit'`을 지원하는 fetch adapter로 받는다.
 */
import type { UserMainApi } from '@ttmo/domain/user-main-api';
import type { AxiosRequestConfig } from 'axios';
import { isMediaCookieOriginSupportedHere } from './media-cookie-environment';

/** 다운로드에 필요한 API 표면이다. */
export type CookielessDownloadApi = {
  helper: Pick<UserMainApi['helper'], 'downloadBinary' | 'xio'>;
};

/** 다운로드 결과다. `downloadBinary`가 돌려주는 값 중 호출부가 쓰는 부분이다. */
export interface CookielessDownloadResult {
  data: ArrayBuffer;
  contentType: string;
}

/** `application/json`과 `application/*+json` 오류 본문을 판별한다. */
const JSON_CONTENT_TYPE_PATTERN = /^application\/(?:[\w.-]+\+)?json(?:\s*;|$)/i;

/**
 * arraybuffer로 받은 JSON 오류 본문을 객체로 되돌려 `error.response.data`에 담는다.
 * xio의 `downloadBinary`는 14.1.0부터 이 변환을 하지만, 직접 `responseType`을 정하는 `getResponse`는 하지 않는다.
 * 객체가 아니면 서버 오류 코드를 읽을 수 없으므로 JSON이 아니거나 해석이 안 되는 본문은 그대로 둔다.
 */
function restoreJsonErrorBody(error: unknown): void {
  const response = (error as { response?: { data?: unknown; headers?: Record<string, unknown> } })
    ?.response;
  // instanceof는 다른 realm(iframe, jsdom)의 ArrayBuffer를 놓치므로 내부 태그로 판별한다.
  if (!response || Object.prototype.toString.call(response.data) !== '[object ArrayBuffer]') return;
  if (!JSON_CONTENT_TYPE_PATTERN.test(String(response.headers?.['content-type'] ?? ''))) return;

  try {
    const body: unknown = JSON.parse(new TextDecoder().decode(response.data as ArrayBuffer));
    if (typeof body === 'object' && body !== null && !Array.isArray(body)) {
      response.data = body;
    }
  } catch {
    // 해석할 수 없는 본문은 원본 ArrayBuffer로 둔다.
  }
}

/**
 * Bearer로 파일을 받는다. 동일 HTTPS origin에서는 flag와 관계없이 쿠키를 보내지 않는다.
 * 다른 출처나 HTTP 환경에서는 기존 `downloadBinary` 호출 그대로 받는다.
 *
 * @example
 * const { data, contentType } = await downloadBinaryWithoutMediaCookie(api, url, { signal });
 * const blob = new Blob([data], { type: contentType });
 *
 * @param api 사용자 API
 * @param url 받을 파일 URL
 * @param options 취소 신호
 * @returns 응답 본문과 Content-Type. 헤더가 없으면 `application/octet-stream`
 */
export async function downloadBinaryWithoutMediaCookie(
  api: CookielessDownloadApi,
  url: string,
  options?: { signal?: AbortSignal },
): Promise<CookielessDownloadResult> {
  if (!isMediaCookieOriginSupportedHere()) {
    return options ? api.helper.downloadBinary(url, options) : api.helper.downloadBinary(url);
  }

  // xio의 downloadBinary는 adapter를 버리므로 axios 설정을 그대로 받는 getResponse를 쓴다.
  const config: AxiosRequestConfig = {
    signal: options?.signal,
    adapter: 'fetch',
    withCredentials: false,
    responseType: 'arraybuffer',
  };
  let response;
  try {
    response = await api.helper.xio.getResponse<ArrayBuffer>(url, config);
  } catch (error) {
    restoreJsonErrorBody(error);
    throw error;
  }
  const contentType = response.headers['content-type'];
  return {
    data: response.data,
    contentType: contentType ? String(contentType) : 'application/octet-stream',
  };
}
